import { Input, Select, Row, Col, Spin, message, Image, Checkbox } from "antd";
import GenderBirthdayModal from "./GenderBirthdayModal";
import CustomerPhones from "../../../components/Customer/CustomerPhones";
import { getCustomerDetail, invalidateCustomerCaches, type CustomerPhoneEntry } from "../../../api/ChannelCustomer";
import { useState, useEffect, useCallback, useImperativeHandle, forwardRef, useRef, useId, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useLocationData } from "../../../hooks/useLocationData";
import { mixGetV1, mixPostV1, mixPostV2 } from "../../../services/mixin";
import { getCustomerPhotos, uploadCustomerPhoto, deleteCustomerPhoto } from "../../../api/CustomerPhoto";
import { filterOptionVietnamese } from "../../../lib/utils";
import { extractApiError } from "../../../lib/apiError";
import useAuthStore from "../../../store/authStore";
import useStoredUser from "../../../hooks/useStoredUser";
import {
    Eye,
    X,
} from "lucide-react";

const { TextArea } = Input;

// API chỉ nhận phone / is_primary / note — bỏ các trường server tự sinh (id, timestamps...)
const normalizePhones = (phones: CustomerPhoneEntry[] = []) =>
    phones.map((p) => ({
        phone: p.phone,
        is_primary: Number(p.is_primary) ? 1 : 0,
        ...(p.note ? { note: p.note } : {}),
    }));

/** Nhãn nhỏ phía trên ô nhập: gõ vào ô (placeholder biến mất) vẫn biết đang sửa trường nào */
const FieldLabel = ({ htmlFor, required, children }: { htmlFor: string; required?: boolean; children: ReactNode }) => (
    <label htmlFor={htmlFor} className="block mb-0.5 text-[11px] font-medium text-gray-500 dark:text-gray-400">
        {children}
        {required && <span className="text-red-500"> *</span>}
    </label>
);

const Field = ({ label, htmlFor, required, children }: { label: ReactNode; htmlFor: string; required?: boolean; children: ReactNode }) => (
    <div>
        <FieldLabel htmlFor={htmlFor} required={required}>{label}</FieldLabel>
        {children}
    </div>
);

interface Props {
    channelCustomerId?: number;
    customer?: { id: number;[key: string]: any } | null;
    displayName?: string;
    onDirtyChange?: (dirty: boolean) => void;
    isReadOnly?: boolean;
    // Gọi khi tạo khách hàng thành công (trả về CRM customer vừa tạo) để cha cập nhật state ngay, không cần tải lại trang
    onCreated?: (customer: any) => void;
    // Đổi giá trị (timestamp) để mở hộp thoại Giới tính & Ngày sinh từ bên ngoài (nút trên header phòng chat)
    openGenderBirthdaySignal?: number;
}

export interface CustomerFormRef {
    save: () => Promise<boolean>;
}

const CustomerForm = forwardRef<CustomerFormRef, Props>(({ channelCustomerId, customer, displayName, onDirtyChange, isReadOnly, onCreated, openGenderBirthdaySignal }, ref) => {
    const { t, i18n } = useTranslation();
    // Tiền tố id cho các ô nhập (gắn nhãn <label htmlFor>), không trùng khi có nhiều form cùng lúc
    const fid = useId();
    const isEditMode = customer != null;
    const [loading, setLoading] = useState(false);
    const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin);

    // Vai trò tài khoản đăng nhập: 1 = Super admin, 2 = Trực page (theo doc /be/staffs)
    const storedUser = useStoredUser();
    const currentUserType = Number(storedUser?.type ?? storedUser?.info?.type ?? 0);
    // Id bản ghi staff của tài khoản đăng nhập:
    // - Tài khoản nhân viên (trực page/telesale...): user.info CHÍNH LÀ bản ghi staff -> id staff = info.id.
    // - Tài khoản admin: info là bản ghi user (info.id là user id) -> chỉ tin info.staff_id.
    const currentStaffId = currentUserType === 1
        ? (Number(storedUser?.info?.staff_id ?? 0) || null)
        : (Number(storedUser?.info?.staff_id ?? storedUser?.info?.id ?? 0) || null);
    // Ràng buộc phân công khi TẠO khách:
    // - Super admin: bắt buộc cả Phân công (trực page) lẫn Phân công Telesale.
    // - Trực page: bắt buộc Phân công Telesale (Phân công tự gán chính mình).
    const staffRequired = !isEditMode && isSuperAdmin;
    const telesaleRequired = !isEditMode && (isSuperAdmin || currentUserType === 2);

    // Tạo lịch hẹn bị chặn do thiếu Tỉnh/TP, Xã/Phường hoặc Địa chỉ
    // -> highlight các ô còn thiếu + focus ô Địa chỉ (event từ CreateOrder)
    const addressRef = useRef<any>(null);
    const [highlightFields, setHighlightFields] = useState<string[]>([]);
    useEffect(() => {
        const onFocusAddress = (e: Event) => {
            const fields: string[] = (e as CustomEvent)?.detail?.fields || ["address"];
            // Chờ panel Collapse mở xong rồi mới cuộn/focus
            setTimeout(() => {
                addressRef.current?.focus?.();
                addressRef.current?.resizableTextArea?.textArea?.scrollIntoView?.({ behavior: "smooth", block: "center" });
            }, 300);
            setHighlightFields(fields);
            setTimeout(() => setHighlightFields([]), 4000);
        };
        window.addEventListener("focus-customer-address", onFocusAddress);
        return () => window.removeEventListener("focus-customer-address", onFocusAddress);
    }, []);
    // Class viền cam nhấn mạnh ô Select (Tỉnh/TP, Xã/Phường) đang thiếu dữ liệu
    const selectHighlightCls = "[&_.ant-select-selector]:!border-orange-500 [&_.ant-select-selector]:!ring-2 [&_.ant-select-selector]:!ring-orange-300";

    // Location data (cached)
    const { provinceOptions, wardOptions, loadWards, setWardOptions } = useLocationData();

    // Dropdown options
    const [users, setUsers] = useState<{ label: string; value: number }[]>([]);
    const [telesales, setTelesales] = useState<{ label: string; value: number }[]>([]);
    const [occupations, setOccupations] = useState<{ label: string; value: number }[]>([]);

    const emptyForm = {
        display_name: displayName || "",
        // Họ tên thật của khách (display_name là tên hiển thị trên kênh chat)
        fullname: "",
        phone: "",
        province_code: null as string | null,
        ward_code: null as string | null,
        address: "",
        note: "",
        customer_tag_id: null as number | null,
        staff_id: null as number | null,
        telesale_id: null as number | null,
        customer_occupation_id: null as number | null,
        // Nguồn gốc khách hàng theo API /be/customers: 0 = trong nước, 1 = nước ngoài.
        // Khách nước ngoài không có Tỉnh/TP - Xã/Phường nên các luồng khác bỏ qua validate 2 ô này.
        is_foreign: 0,
        // Giới tính theo API: 0 = không xác định, 1 = Nam, 2 = Nữ. Ngày sinh dạng YYYY-MM-DD.
        gender: 0,
        birthday: "" as string,
        // Số điện thoại phụ (param `phones`): gửi cả mảng, backend ghi đè danh sách
        phones: [] as CustomerPhoneEntry[],
    };

    // Form state
    const [formData, setFormData] = useState(emptyForm);

    // Original data for dirty detection
    const [originalData, setOriginalData] = useState(emptyForm);

    const isDirty = JSON.stringify(formData) !== JSON.stringify(originalData);

    // Đổi Telesale của khách ĐÃ TẠO: chỉ Super Admin hoặc trực page đang phụ trách chính khách này
    // (giống trang chi tiết khách hàng). Lúc tạo khách mới thì vẫn phải chọn được Telesale.
    const canEditTelesale = !isEditMode
        || isSuperAdmin
        || (!!currentStaffId && Number(formData.staff_id) === currentStaffId);

    useEffect(() => {
        onDirtyChange?.(isDirty);
    }, [isDirty, onDirtyChange]);

    // Fetch real customer detail (edit mode)
    const fetchCustomerDetail = useCallback(async () => {
        if (!isEditMode || !customer?.id) return;
        setLoading(true);
        // Dùng chung request với khung thông tin khách (CustomerDropdown) cũng đang lấy hồ sơ này
        const res = await getCustomerDetail(customer.id);
        if (res.status && res.data) {
            const d: any = res.data;
            const newFormData = {
                display_name: d.display_name || d.name || "",
                fullname: d.fullname || "",
                phone: d.phone || "",
                province_code: d.province_code || d.province?.code || null,
                ward_code: d.ward_code || d.ward?.code || null,
                address: d.address || "",
                note: d.note || "",
                customer_tag_id: d.customer_tag_id ?? null,
                staff_id: d.staff_id ?? null,
                telesale_id: d.telesale_id ?? null,
                customer_occupation_id: d.customer_occupation_id ?? null,
                is_foreign: Number(d.is_foreign) === 1 ? 1 : 0,
                gender: Number(d.gender) || 0,
                // API trả "YYYY-MM-DD" hoặc dạng ISO -> chỉ giữ phần ngày
                birthday: d.birthday ? String(d.birthday).slice(0, 10) : "",
                phones: Array.isArray(d.phones) ? d.phones : [],
            };
            setFormData(newFormData);
            setOriginalData(newFormData);
            if (d.province_code || d.province?.code) {
                loadWards(d.province_code || d.province.code);
            }
        }
        setLoading(false);
    }, [isEditMode, customer?.id, loadWards]);

    // Reset form when switching between channel customers
    useEffect(() => {
        if (!isEditMode) {
            const fresh = { ...emptyForm, display_name: displayName || "" };
            setFormData(fresh);
            setOriginalData(fresh);
        }
    }, [isEditMode, channelCustomerId]);

    // Fetch dropdown options
    const fetchOptions = useCallback(async () => {
        try {
            const [occRes, userRes, telesaleRes] = await Promise.all([
                mixGetV1('/api/v1/be/customer-occupations', { take: 100 }).catch(() => null),
                mixGetV1('/api/v1/be/staffs', { type_user: 1, limit: 200, type_department_id: 2 }).catch(() => null),
                mixGetV1('/api/v1/be/staffs', { type_user: 1, limit: 200, type_department_id: 3 }).catch(() => null),
            ]);

            if (occRes?.status && occRes.data?.items) {
                setOccupations(occRes.data.items.map((o: any) => ({ label: o.name, value: o.id })));
            }
            if (userRes?.status && userRes.data?.items) {
                setUsers(userRes.data.items.map((u: any) => ({ label: u.name, value: u.id })));
            }
            if (telesaleRes?.status && telesaleRes.data?.items) {
                setTelesales(telesaleRes.data.items.map((u: any) => ({ label: u.name, value: u.id })));
            }
        } catch (error) {
            console.error("Error fetching options:", error);
        }
    }, []);

    useEffect(() => {
        fetchOptions();
    }, [fetchOptions]);

    useEffect(() => {
        fetchCustomerDetail();
    }, [fetchCustomerDetail]);

    // Tài khoản Trực page tạo khách mới: tự gán chính mình vào Phân công (ô này chỉ hiển thị cho super admin)
    useEffect(() => {
        if (isEditMode || isReadOnly) return;
        if (currentUserType === 2 && currentStaffId) {
            setFormData((prev) => (prev.staff_id ? prev : { ...prev, staff_id: currentStaffId }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditMode, isReadOnly, currentUserType, currentStaffId]);

    // Ảnh trong ô Ghi chú:
    // - TẠO MỚI: giữ File local -> gửi photos[] khi tạo khách.
    // - ĐÃ TỒN TẠI: đồng bộ realtime qua API hình ảnh khách hàng (list/upload/delete).
    type NotePhoto = { uid: string; id?: number; file?: File; url: string; uploading?: boolean };
    const [notePhotos, setNotePhotos] = useState<NotePhoto[]>([]);
    // Badge "Sẵn sàng dán" chỉ hiện khi clipboard thật sự đang chứa ảnh
    const [clipboardHasImage, setClipboardHasImage] = useState(false);

    const checkClipboardImage = useCallback(async () => {
        if (isReadOnly) { setClipboardHasImage(false); return; }
        try {
            // clipboard.read cần secure context + document đang focus
            if (!navigator.clipboard?.read || !document.hasFocus()) return;
            const items = await navigator.clipboard.read();
            const hasImg = items.some((it) => it.types.some((t) => t.startsWith("image/")));
            setClipboardHasImage(hasImg);
        } catch {
            // Không có quyền đọc clipboard -> bỏ qua, không hiện badge
        }
    }, [isReadOnly]);

    // Kiểm tra lại mỗi khi cửa sổ/tab được focus lại (vd vừa copy ảnh ở app khác)
    // và khi bấm nút "Sao chép ảnh" trên khung chat (qua custom event).
    useEffect(() => {
        const onWinFocus = () => checkClipboardImage();
        const onImageCopied = () => { if (!isReadOnly) setClipboardHasImage(true); };
        window.addEventListener("focus", onWinFocus);
        window.addEventListener("clipboard-image-copied", onImageCopied);
        checkClipboardImage();
        return () => {
            window.removeEventListener("focus", onWinFocus);
            window.removeEventListener("clipboard-image-copied", onImageCopied);
        };
    }, [checkClipboardImage, isReadOnly]);
    const newUid = () => `${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const maxPhotoSize = isEditMode ? 5 * 1024 * 1024 : 10 * 1024 * 1024; // upload 5MB, tạo mới 10MB

    // Đổi khách: dọn ảnh cũ; nếu là khách đã tồn tại -> nạp danh sách ảnh từ API
    useEffect(() => {
        setNotePhotos((prev) => {
            prev.forEach((p) => { if (p.file) URL.revokeObjectURL(p.url); });
            return [];
        });
        if (isEditMode && customer?.id) {
            getCustomerPhotos(customer.id, { limit: 100 }).then((res) => {
                if (res.status) {
                    setNotePhotos(res.items.map((it) => ({ uid: newUid(), id: it.id, url: it.image })));
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditMode, customer?.id, channelCustomerId]);

    const handleNotePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
        if (isReadOnly) return;
        const items = e.clipboardData?.items;
        if (!items) return;
        const files: File[] = [];
        let tooLarge = false;
        Array.from(items).forEach((item) => {
            if (item.type.startsWith("image/")) {
                const file = item.getAsFile();
                if (!file) return;
                if (file.size > maxPhotoSize) { tooLarge = true; return; }
                files.push(file);
            }
        });
        if (tooLarge) message.error(t("roomchat.image_too_large", { size: isEditMode ? 5 : 10 }));
        if (!files.length) return;
        e.preventDefault(); // không chèn dữ liệu ảnh dạng text vào note
        setClipboardHasImage(false); // đã dán ảnh ra -> ẩn badge "Sẵn sàng dán"

        const cid = customer?.id;
        if (isEditMode && cid) {
            // Khách đã tồn tại -> upload ngay lên API (optimistic + revert khi lỗi)
            files.forEach(async (file) => {
                const uid = newUid();
                const url = URL.createObjectURL(file);
                setNotePhotos((prev) => [...prev, { uid, file, url, uploading: true }]);
                const res = await uploadCustomerPhoto(cid, file);
                if (res.status && res.data) {
                    URL.revokeObjectURL(url);
                    setNotePhotos((prev) =>
                        prev.map((p) => (p.uid === uid ? { uid, id: res.data!.id, url: res.data!.image } : p))
                    );
                } else {
                    URL.revokeObjectURL(url);
                    setNotePhotos((prev) => prev.filter((p) => p.uid !== uid));
                    message.error(res.msg || "Tải ảnh lên thất bại");
                }
            });
        } else {
            // Tạo mới: giữ file local để gửi photos[] khi tạo
            setNotePhotos((prev) => [
                ...prev,
                ...files.map((file) => ({ uid: newUid(), file, url: URL.createObjectURL(file) })),
            ]);
        }
    };

    const removeNotePreview = async (uid: string) => {
        const p = notePhotos.find((x) => x.uid === uid);
        if (!p) return;
        // Khách đã tồn tại + ảnh đã có trên server -> gọi API xóa
        if (isEditMode && customer?.id && p.id) {
            const res = await deleteCustomerPhoto(p.id);
            if (!res.status) { message.error(res.msg || "Xóa ảnh thất bại"); return; }
        }
        if (p.file) URL.revokeObjectURL(p.url);
        setNotePhotos((prev) => prev.filter((x) => x.uid !== uid));
    };

    /* ===== Giới tính & Ngày sinh =====
       Sửa trong hộp thoại riêng (GenderBirthdayModal). Khách đã có hồ sơ CRM thì
       hộp thoại tự lưu lên API; khách chưa tạo thì giá trị nằm trong form và được
       gửi kèm lúc tạo khách. */
    const [openBirthModal, setOpenBirthModal] = useState(false);

    const genderLabel = (value: number) => {
        if (value === 1) return t("roomchat.male") || "Nam";
        if (value === 2) return t("roomchat.female") || "Nữ";
        if (value === 3) return t("customer.other_gender") || "Khác";
        return t("roomchat.gender_unknown") || "Không xác định";
    };
    // YYYY-MM-DD -> DD/MM/YYYY để hiển thị
    const displayBirthday = (value?: string) => {
        if (!value) return "";
        const [y, m, d] = value.split("-");
        return y && m && d ? [d, m, y].join("/") : value;
    };

    const handleSavedGenderBirthday = ({ gender, birthday }: { gender: number; birthday: string }) => {
        setFormData((prev) => ({ ...prev, gender, birthday }));
        // Khách đã có hồ sơ CRM -> hộp thoại đã lưu lên server, dời luôn mốc so sánh
        // để form không bị coi là đang sửa dở.
        if (isEditMode && customer?.id) {
            setOriginalData((prev) => ({ ...prev, gender, birthday }));
        }
    };

    // Nút "Giới tính và Ngày sinh" trên header phòng chat bắn tín hiệu qua đây
    useEffect(() => {
        if (!openGenderBirthdaySignal) return;
        setOpenBirthModal(true);
    }, [openGenderBirthdaySignal]);
    const handleFieldChange = (field: string, value: any) => {
        // Chặn thêm ở tầng xử lý phòng khi ô Select bị mở khoá bằng cách khác
        if (field === "telesale_id" && !canEditTelesale) {
            message.warning(t("roomchat.telesale_not_allowed") || "Bạn chỉ đổi được Telesale của khách hàng do mình phụ trách");
            return;
        }
        const newFormData = { ...formData, [field]: value };
        if (field === "province_code") {
            newFormData.ward_code = null;
            setWardOptions([]);
            if (value) loadWards(value);
        }
        setFormData(newFormData);
    };

    // Save: create or update
    const handleSave = async (): Promise<boolean> => {
        if (!isDirty && isEditMode) return true;

        if (!isEditMode) {
            // Bắt buộc phân công khi tạo khách (super admin: cả 2 ô; trực page: ô Telesale)
            const missingAssign: string[] = [];
            if (staffRequired && !formData.staff_id) missingAssign.push("staff_id");
            if (telesaleRequired && !formData.telesale_id) missingAssign.push("telesale_id");
            if (missingAssign.length > 0) {
                const labels: Record<string, string> = {
                    staff_id: t("roomchat.assign") || "Phân công",
                    telesale_id: t("roomchat.telesale") || "Phân công Telesale",
                };
                const fields = missingAssign.map((f) => labels[f]).join(", ");
                message.warning(
                    t("roomchat.customer_need_assign", { fields })
                    || `Vui lòng chọn ${fields} trước khi tạo khách hàng`
                );
                setHighlightFields(missingAssign);
                setTimeout(() => setHighlightFields([]), 4000);
                return false;
            }

            // Tỉnh/TP, Xã/Phường và Địa chỉ KHÔNG bắt buộc khi tạo khách — có thể bổ sung sau.
            // (Riêng lúc tạo lịch hẹn vẫn cần đủ địa chỉ, xem CustomerInfo/CreateOrder.tsx)

            // Create new customer
            const payload: any = { ...formData, channel_customers_ids: channelCustomerId ? [channelCustomerId] : [] };
            // Ngày sinh rỗng -> bỏ hẳn khỏi payload cho khỏi vướng validate định dạng date
            if (!payload.birthday) delete payload.birthday;
            // Số điện thoại phụ: chưa có số nào thì khỏi gửi, có thì chỉ gửi đúng 3 trường API nhận
            if (!payload.phones?.length) delete payload.phones;
            else payload.phones = normalizePhones(payload.phones);

            // Có ảnh dán vào ghi chú -> gửi kèm photos[] qua multipart (mixPostV2)
            const photoFiles = notePhotos.map((p) => p.file).filter(Boolean) as File[];
            const hasPhotos = photoFiles.length > 0;
            if (hasPhotos) payload.photos = photoFiles;

            const res = hasPhotos
                ? await mixPostV2('/api/v1/be/customers', payload)
                : await mixPostV1('/api/v1/be/customers', payload);
            invalidateCustomerCaches();
            if (res.status) {
                message.success(t("roomchat.create_success") || "Tạo khách hàng thành công");
                setOriginalData({ ...formData });
                // dọn ảnh đã gửi
                setNotePhotos((prev) => { prev.forEach((p) => { if (p.file) URL.revokeObjectURL(p.url); }); return []; });
                // Đẩy CRM customer vừa tạo lên cha để DOM cập nhật ngay (bật nút "Tạo lịch hẹn", đổi nút thành "Cập nhật")
                if (res.data) onCreated?.(res.data);
                return true;
            } else {
                // Ưu tiên hiển thị đúng lỗi API trả về (đã map sang tiếng Việt), nếu không có thì dùng thông báo mặc định
                const apiErr = extractApiError(res, i18n.language);
                message.error(apiErr || t("roomchat.create_failed") || "Tạo khách hàng thất bại");
                return false;
            }
        } else {
            // Update existing customer
            const changedFields: Record<string, any> = { display_name: formData.display_name };
            for (const key of Object.keys(formData) as (keyof typeof formData)[]) {
                if (formData[key] !== originalData[key]) {
                    changedFields[key] = formData[key];
                }
            }
            // Xóa ngày sinh -> gửi null thay vì chuỗi rỗng (API nhận date hoặc null)
            if (changedFields.birthday === "") changedFields.birthday = null;
            if (changedFields.phones) changedFields.phones = normalizePhones(changedFields.phones);
            // Endpoint cập nhật khách hàng dùng POST (PUT trả 405). Khớp với updateCustomer() sẵn có.
            const res = await mixPostV1(`/api/v1/be/customers/${customer!.id}`, changedFields);
            invalidateCustomerCaches();
            if (res.status) {
                setOriginalData({ ...formData });
                message.success(t("roomchat.update_success") || "Cập nhật thành công");
                return true;
            } else {
                message.error(t("roomchat.update_failed"));
                return false;
            }
        }
    };

    useImperativeHandle(ref, () => ({
        save: handleSave,
    }));

    return (
        <Spin spinning={loading}>
            <div className="space-y-2">

                <Field label={t("roomchat.customer_name")} htmlFor={`${fid}-display_name`}>
                <Input
                    id={`${fid}-display_name`}
                    placeholder={t("roomchat.customer_name")}
                    value={formData.display_name}
                    onChange={(e) => handleFieldChange("display_name", e.target.value)}
                    className="dark:bg-[#1f1f1f] dark:border-[#303030] dark:text-white"
                    disabled={isReadOnly}
                />
                </Field>

                <Field label={t("roomchat.customer_fullname") || "Họ tên thật"} htmlFor={`${fid}-fullname`}>
                <Input
                    id={`${fid}-fullname`}
                    placeholder={t("roomchat.customer_fullname") || "Họ tên thật"}
                    value={formData.fullname}
                    onChange={(e) => handleFieldChange("fullname", e.target.value)}
                    className="dark:bg-[#1f1f1f] dark:border-[#303030] dark:text-white"
                    disabled={isReadOnly}
                />
                </Field>

                <Field label={t("roomchat.phone")} htmlFor={`${fid}-phone`}>
                <Input
                    id={`${fid}-phone`}
                    placeholder={t("roomchat.phone")}
                    value={formData.phone}
                    onChange={(e) => handleFieldChange("phone", e.target.value)}
                    className="dark:bg-[#1f1f1f] dark:border-[#303030] dark:text-white"
                    disabled={isReadOnly}
                />
                </Field>

                {/* Số điện thoại phụ: giữ trong form, lưu cùng lúc bấm Thêm/Cập nhật khách hàng */}
                {!isReadOnly && (
                    <CustomerPhones
                        phones={formData.phones}
                        mainPhone={formData.phone}
                        onSave={(next) => handleFieldChange("phones", next)}
                    />
                )}

                {/* Giới tính & Ngày sinh: hiển thị gọn 1 dòng, sửa trong hộp thoại riêng */}
                <div className="flex items-center justify-between gap-2 text-[12px] text-gray-600 dark:text-gray-300">
                    <span className="truncate">
                        {genderLabel(formData.gender)}
                        {formData.birthday ? ` · ${displayBirthday(formData.birthday)}` : ""}
                        {!formData.birthday && (
                            <span className="text-gray-400"> · {t("roomchat.birthday_empty") || "Chưa có ngày sinh"}</span>
                        )}
                    </span>
                </div>

                {/* Khách nước ngoài (is_foreign): vẫn nhập được Tỉnh/TP - Xã/Phường bình thường,
                    chỉ khác ở chỗ luồng tạo lịch hẹn bỏ qua validate địa giới cho khách này */}
                <Checkbox
                    checked={formData.is_foreign === 1}
                    onChange={(e) => handleFieldChange("is_foreign", e.target.checked ? 1 : 0)}
                    disabled={isReadOnly}
                    className="dark:text-white"
                >
                    {t("roomchat.foreign_customer") || "Khách nước ngoài"}
                </Checkbox>

                <Row gutter={8}>
                    <Col span={12}>
                        <FieldLabel htmlFor={`${fid}-province`}>{t("roomchat.province")}</FieldLabel>
                        <Select
                            id={`${fid}-province`}
                            placeholder={t("roomchat.province")}
                            className={`w-full ${highlightFields.includes("province_code") ? selectHighlightCls : ""}`}
                            value={formData.province_code}
                            onChange={(val) => handleFieldChange("province_code", val)}
                            options={provinceOptions}
                            showSearch
                            filterOption={filterOptionVietnamese}
                            allowClear
                            disabled={isReadOnly}
                        />
                    </Col>

                    <Col span={12}>
                        <FieldLabel htmlFor={`${fid}-ward`}>{t("roomchat.ward")}</FieldLabel>
                        <Select
                            id={`${fid}-ward`}
                            placeholder={t("roomchat.ward")}
                            className={`w-full ${highlightFields.includes("ward_code") ? selectHighlightCls : ""}`}
                            value={formData.ward_code}
                            onChange={(val) => handleFieldChange("ward_code", val)}
                            options={wardOptions}
                            showSearch
                            filterOption={filterOptionVietnamese}
                            allowClear
                            disabled={isReadOnly}
                        />
                    </Col>
                </Row>

                <FieldLabel htmlFor={`${fid}-address`}>{t("roomchat.address")}</FieldLabel>
                <TextArea
                    id={`${fid}-address`}
                    ref={addressRef}
                    rows={2}
                    placeholder={t("roomchat.address")}
                    value={formData.address}
                    onChange={(e) => handleFieldChange("address", e.target.value)}
                    className={`dark:bg-[#1f1f1f] dark:border-[#303030] dark:text-white transition-shadow ${highlightFields.includes("address") ? "!border-orange-500 ring-2 ring-orange-300" : ""}`}
                    disabled={isReadOnly}
                />

                <div>
                    {/* Hàng nhãn cố định chiều cao + badge fade để không gây giật layout */}
                    <div className="flex justify-between items-center mb-1 h-[16px]">
                        <label htmlFor={`${fid}-note`} className="text-[10px] font-bold text-blue-500 dark:text-blue-400">
                            {isReadOnly ? t("roomchat.note_label") : t("roomchat.note_paste_hint")}
                        </label>
                        {!isReadOnly && (
                            <span
                                className={`text-[9px] bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 px-1.5 py-0.5 rounded-full flex items-center gap-1 transition-opacity duration-200 ${clipboardHasImage ? "opacity-100" : "opacity-0 pointer-events-none"}`}
                            >
                                <span className="w-1.5 h-1.5 rounded-full bg-blue-500 dark:bg-blue-300 animate-pulse" />
                                {t("roomchat.ready_to_paste")}
                            </span>
                        )}
                    </div>
                    <TextArea
                        id={`${fid}-note`}
                        rows={2}
                        placeholder={t("roomchat.note")}
                        value={formData.note}
                        onChange={(e) => handleFieldChange("note", e.target.value)}
                        onPaste={handleNotePaste}
                        onFocus={checkClipboardImage}
                        className="dark:bg-[#1f1f1f] dark:border-[#303030] dark:text-white"
                        disabled={isReadOnly}
                    />

                    {notePhotos.length > 0 && (
                        <Image.PreviewGroup>
                            <div className="flex gap-2.5 flex-wrap mt-2.5">
                                {notePhotos.map((p) => (
                                    <div
                                        key={p.uid}
                                        className="relative group w-[68px] h-[68px] rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 shadow-sm transition-all hover:shadow-md hover:border-[#3e91ab]"
                                    >
                                        <Image
                                            src={p.url}
                                            width={68}
                                            height={68}
                                            rootClassName="w-full h-full"
                                            className="object-cover w-full h-full"
                                            preview={{
                                                mask: (
                                                    <div className="flex items-center justify-center">
                                                        <Eye size={18} />
                                                    </div>
                                                )
                                            }}
                                        />
                                        {p.uploading && (
                                            <div className="absolute inset-0 bg-black/45 flex items-center justify-center">
                                                <Spin size="small" />
                                            </div>
                                        )}
                                        {!isReadOnly && !p.uploading && (
                                            <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); removeNotePreview(p.uid); }}
                                                className="absolute top-1 right-1 z-10 w-5 h-5 rounded-full bg-black/55 hover:bg-red-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all"
                                            >
                                                <X size={12} strokeWidth={2.5} />
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </Image.PreviewGroup>
                    )}
                </div>

                {isSuperAdmin && (
                    <Field label={t("roomchat.assign")} required={staffRequired} htmlFor={`${fid}-staff`}>
                    <Select
                        id={`${fid}-staff`}
                        placeholder={`${t("roomchat.assign")}${staffRequired ? " *" : ""}`}
                        className={`w-full ${highlightFields.includes("staff_id") ? selectHighlightCls : ""}`}
                        value={formData.staff_id}
                        onChange={(val) => handleFieldChange("staff_id", val)}
                        options={users}
                        showSearch
                        filterOption={filterOptionVietnamese}
                        allowClear
                        disabled={isReadOnly}
                    />
                    </Field>
                )}

                <Field label={t("roomchat.telesale") || "Phân công Telesale"} required={telesaleRequired} htmlFor={`${fid}-telesale`}>
                <Select
                    id={`${fid}-telesale`}
                    placeholder={`${t("roomchat.telesale") || "Phân công Telesale"}${telesaleRequired ? " *" : ""}`}
                    className={`w-full ${highlightFields.includes("telesale_id") ? selectHighlightCls : ""}`}
                    value={formData.telesale_id}
                    onChange={(val) => handleFieldChange("telesale_id", val)}
                    options={telesales}
                    showSearch
                    filterOption={filterOptionVietnamese}
                    allowClear
                    disabled={isReadOnly || !canEditTelesale}
                />
                </Field>

                <Field label={t("roomchat.occupation")} htmlFor={`${fid}-occupation`}>
                <Select
                    id={`${fid}-occupation`}
                    placeholder={t("roomchat.occupation")}
                    className="w-full"
                    value={formData.customer_occupation_id}
                    onChange={(val) => handleFieldChange("customer_occupation_id", val)}
                    options={occupations}
                    allowClear
                    disabled={isReadOnly}
                />
                </Field>

                <GenderBirthdayModal
                    open={openBirthModal}
                    onClose={() => setOpenBirthModal(false)}
                    gender={formData.gender}
                    birthday={formData.birthday}
                    customerId={customer?.id ?? null}
                    displayName={formData.display_name}
                    onSaved={handleSavedGenderBirthday}
                />

            </div>
        </Spin>
    );
});

export default CustomerForm;